import $ls, { LocalStorageKey } from "@/utils/local-storage";
import { reactive } from "vue";
import type { UserProperties } from "../APIClient";
import { DEFAULT_LEGEND } from "../defaultLegend";
import type { Legend } from "@/types/legend.types";
import { projectRepository } from "@/repositories/project.repository";
import { drawingRepository } from "@/repositories/drawing.repository";
import { backupRepository } from "@/repositories/backup.repository";

type UserState = {
  data: null | UserProperties;
  isLoggedIn: boolean;
  isInternalUser: () => boolean;
  isSuperAdmin: () => boolean;
  isAdmin: () => boolean;
  updateAndPersist: (user: UserProperties) => void;
  setupRepositories: (user: UserProperties) => void;
  initialize: () => void;
  reset: () => void;
  getLegends: () => Legend[];
  hasSeenRenderAlert: boolean;
};

export default reactive<UserState>({
  data: null,
  isLoggedIn: false,
  hasSeenRenderAlert: false,

  setupRepositories(user: UserProperties) {
    backupRepository.setCurrentUserId(user.id);
    if (user.organization) {
      projectRepository.setCurrentOrganizationId(user.organization.id);
      drawingRepository.setCurrentOrganizationId(user.organization.id);
    }
  },

  /**
   * Update the user state and persist to LocalStorage.
   */
  updateAndPersist(user: UserProperties) {
    this.data = user;
    this.isLoggedIn = true;
    this.setupRepositories(user);
    $ls.setItem(LocalStorageKey.USER, JSON.stringify(this.data));

    // if user has no legends, set a default one
    if (!user.legends || !user.legends.length) {
      this.data.legends = [DEFAULT_LEGEND];
    }
  },

  /**
   *  Initializes the user state from LocalStorage.
   */
  initialize() {
    const storedUser = $ls.getItem(LocalStorageKey.USER);
    if (storedUser) {
      const user = JSON.parse(storedUser) as UserProperties;
      if (user.id) {
        this.data = user;
        this.isLoggedIn = true;
        this.setupRepositories(user);
      } else {
        // in this case, it means that the stored user doens't have the necessary information
        // we might as well clean it from the storage
        $ls.removeItem(LocalStorageKey.USER);
      }
    }
  },

  reset() {
    $ls.removeItem(LocalStorageKey.USER);
    $ls.removeItem(LocalStorageKey.JWT);
    this.isLoggedIn = false;
    this.data = null;
  },
  isInternalUser() {
    if (import.meta.env.DEV) return true;
    const rgs = [/@diag(nosticrapid)?.(fr|com)$/];
    return rgs.some((rg) => this.data?.email && rg.test(this.data?.email));
  },
  isSuperAdmin() {
    return this.data?.role === "SUPERADMIN";
  },
  isAdmin() {
    return this.data?.role === "ADMIN" || this.isSuperAdmin();
  },
  getLegends(): Legend[] {
    return JSON.parse($ls.getItem(LocalStorageKey.LEGENDS) || "[]") as Legend[];
  },
});
